/**
 * RIS Viewer Modal Component
 *
 * Modal for displaying RIS documents with intelligent highlighting.
 * Features loading states, error handling, and toolbar actions.
 */

import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
  ClipboardDocumentIcon,
  ArrowPathIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { buildApiUrl } from '../config/api';
import { useRISViewer } from '../contexts/RISViewerContext';
import { sanitizeRisHtml } from '../utils/sanitize';
import { normalizeOfficialRisUrl, safeHttpUrl } from '../services/risViewerLinks';
import { seiteSperren } from '../administration/components';

// pdf.js wiegt schwer und wird nur für die PDF-Vorschau gebraucht, deshalb
// wird die Vorschau erst beim ersten Öffnen nachgeladen.
const DocumentPdfPreview = React.lazy(() => import('./DocumentPdfPreview'));

/**
 * Verortete Passagen in Dokumentreihenfolge: nach Seite, bei gleicher Seite
 * nach Rang der Passage. Diese Reihenfolge ist die einzige Wahrheit für
 * Zähler und Schrittsteuerung; die Vorschau bekommt ihre Position von hier.
 */
export function orderedLocatedPassages(locations: Record<number, number>): number[] {
  return Object.entries(locations)
    .map(([index, page]) => ({ index: Number(index), page }))
    .sort((a, b) => a.page - b.page || a.index - b.index)
    .map(entry => entry.index);
}

/**
 * Position der am höchsten gereihten verorteten Passage (kleinster Rang) in
 * der Dokumentreihenfolge, oder -1, wenn noch nichts verortet ist. Damit
 * öffnet die Vorschau beim besten Treffer statt beim ersten im Seitenlauf.
 */
export function initialPassagePosition(locations: Record<number, number>): number {
  const ordered = orderedLocatedPassages(locations);
  if (ordered.length === 0) return -1;
  return ordered.indexOf(Math.min(...ordered));
}

/**
 * Marke, bei der die Textansicht aufschlägt: die erste direkt relevante
 * (gelbe). Eine Auswahlliste träfe sonst die erste Marke im Dokument, auch
 * wenn sie nur Kontext ist. Ohne gelbe Marke bleibt es bei der ersten.
 */
export function initialTextHighlightIndex(classes: readonly string[]): number {
  const primary = classes.findIndex(value => value.split(/\s+/).includes('highlight-primary'));
  return primary >= 0 ? primary : 0;
}

/**
 * DR-13 UI-32: der Seitentitel des RIS lautet „RIS - <Dokument> - <Anwendung>“, etwa „RIS - Zivilprozessordnung § 125 -
 * Bundesrecht konsolidiert“. Die Ansicht zeigt nur das Dokument; jeder andere Titel bleibt, wie er ist.
 */
export function dokumentTitel(titel: string): string {
  const teile = titel.split(' - ');
  return teile.length >= 3 && teile[0].trim() === 'RIS' ? teile.slice(1, -1).join(' - ') : titel;
}

const RISViewerModal: React.FC = () => {
  const { state, closeViewer, refreshViewer } = useRISViewer();
  const location = useLocation();
  const contentRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const routeRef = useRef(`${location.pathname}${location.search}`);
  const [copied, setCopied] = React.useState(false);
  const [highlightIndex, setHighlightIndex] = React.useState(0);
  const [viewMode, setViewMode] = React.useState<'pdf' | 'text'>('pdf');
  // Passagen-Index der Passage auf die Seitenzahl, auf der sie gefunden wurde.
  const [pdfLocations, setPdfLocations] = React.useState<Record<number, number>>({});
  const [pdfPosition, setPdfPosition] = React.useState(0);
  const [pdfError, setPdfError] = React.useState<string | null>(null);
  /** Ob der Nutzer die Stelle in der PDF-Vorschau selbst gewechselt hat. */
  const pdfNavigatedRef = useRef(false);

  const scrollToHighlight = (index: number) => {
    const highlights = contentRef.current?.querySelectorAll('.highlight-primary, .highlight-secondary');
    const highlight = highlights?.item(index);
    if (highlight) highlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  // Scroll to first highlight when document loads
  useEffect(() => {
    setPdfLocations({});
    setPdfPosition(0);
    setPdfError(null);
    pdfNavigatedRef.current = false;
    if (state.data && contentRef.current) {
      const highlights = contentRef.current.querySelectorAll('.highlight-primary, .highlight-secondary');
      const startPosition = initialTextHighlightIndex(
        Array.from(highlights, node => node.className),
      );
      const firstHighlight = highlights.item(startPosition);
      setHighlightIndex(startPosition);
      if (firstHighlight) {
        setTimeout(() => {
          firstHighlight.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
  }, [state.data]);

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && state.isOpen) {
        closeViewer();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [state.isOpen, closeViewer]);

  // Prevent body scroll when modal is open (DR-13 UI-04: dieselbe zählende Sperre wie die Dialoge)
  useEffect(() => (state.isOpen ? seiteSperren() : undefined), [state.isOpen]);

  useEffect(() => {
    if (!state.isOpen) return;
    const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // F-27: die Ansicht steht in der Hülle (AppLayout) und nimmt deren zoom; gesperrt wird alles neben ihr, nicht die Hülle selbst.
    const backdrop = backdropRef.current;
    const appShellParts = Array.from(backdrop?.parentElement?.children ?? [])
      .filter((element): element is HTMLElement => element instanceof HTMLElement && element !== backdrop);
    const wasInert = appShellParts.map(part => part.inert);
    appShellParts.forEach(part => { part.inert = true; });
    const focusFrame = requestAnimationFrame(() => closeButtonRef.current?.focus());
    const trapFocus = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ));
      if (focusable.length === 0) {
        event.preventDefault();
        closeButtonRef.current?.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', trapFocus);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', trapFocus);
      appShellParts.forEach((part, index) => { part.inert = wasInert[index]; });
      if (previouslyFocused?.isConnected) previouslyFocused.focus();
    };
  }, [state.isOpen]);

  const handlePassageLocated = React.useCallback((index: number, page: number) => {
    setPdfLocations(previous => (previous[index] === page ? previous : { ...previous, [index]: page }));
  }, []);

  // Die Verortung läuft seitenweise, die beste Passage kann also erst spät
  // auftauchen. Solange der Nutzer nicht selbst gewechselt hat, folgt die
  // Anfangsposition dem jeweils besten bekannten Treffer.
  useEffect(() => {
    if (pdfNavigatedRef.current) return;
    const position = initialPassagePosition(pdfLocations);
    if (position < 0) return;
    setPdfPosition(previous => (previous === position ? previous : position));
  }, [pdfLocations]);

  // Scheitert die PDF-Vorschau, wird ohne Zutun auf die Textansicht gewechselt.
  const handlePdfError = React.useCallback((message: string) => {
    setPdfError(message);
    setViewMode('text');
  }, []);

  // A route change leaves the provider mounted. Close the modal and abort its
  // request so a response from the previous page cannot appear afterwards.
  useEffect(() => {
    const route = `${location.pathname}${location.search}`;
    if (routeRef.current !== route) {
      routeRef.current = route;
      if (state.isOpen) closeViewer();
    }
  }, [location.pathname, location.search, state.isOpen, closeViewer]);

  if (!state.isOpen) {
    return null;
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeViewer();
    }
  };

  const handleOpenExternal = () => {
    // For KV documents, use the WKO source URL; for RIS docs, use the RIS URL
    const candidate = state.data?.metadata?.sourceUrl || state.url;
    const externalUrl = normalizeOfficialRisUrl(candidate) ?? safeHttpUrl(candidate);
    if (externalUrl) {
      window.open(externalUrl, '_blank', 'noopener,noreferrer');
    }
  };

  /**
   * Reiner Text des Dokuments. In der PDF-Ansicht ist die Textansicht
   * ausgeblendet; innerText liefert dann keine Zeilenumbrüche mehr, deshalb
   * werden die innersten Blöcke selbst getrennt.
   */
  const documentPlainText = (element: HTMLElement): string => {
    if (element.getClientRects().length > 0) return element.innerText;
    const blocks = 'p, div, li, tr, h1, h2, h3, h4, h5, h6, pre, blockquote';
    const parts: string[] = [];
    element.querySelectorAll<HTMLElement>(blocks).forEach(node => {
      if (node.querySelector(blocks)) return;
      const text = (node.textContent ?? '').trim();
      if (text) parts.push(text);
    });
    return parts.length > 0 ? parts.join('\n\n') : (element.textContent ?? '').trim();
  };

  const handleCopyContent = async () => {
    if (contentRef.current) {
      try {
        const text = documentPlainText(contentRef.current);
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const renderLoading = () => (
    <div className="flex flex-col items-center justify-center h-96 space-y-4">
      <div className="relative">
        <div className="w-16 h-16 border-4 border-warm-200 dark:border-gray-700 rounded-full animate-spin border-t-primary dark:border-t-accent"></div>
      </div>
      <div className="text-center">
        <p className="text-gray-700 dark:text-gray-300 font-medium">Dokument wird geladen...</p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Relevante Stellen werden identifiziert
        </p>
      </div>
    </div>
  );

  const renderError = () => (
    <div className="flex flex-col items-center justify-center h-96 space-y-4 p-6">
      <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
        <XMarkIcon className="w-8 h-8 text-red-600 dark:text-red-400" />
      </div>
      <div className="text-center">
        <p className="text-gray-900 dark:text-gray-100 font-medium">Fehler beim Laden</p>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-md">
          {state.error || 'Das Dokument konnte nicht geladen werden.'}
        </p>
      </div>
      <div className="flex space-x-3">
        <button
          onClick={refreshViewer}
          className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark transition-colors flex items-center space-x-2"
        >
          <ArrowPathIcon className="w-4 h-4" />
          <span>Erneut versuchen</span>
        </button>
        <button
          onClick={handleOpenExternal}
          className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex items-center space-x-2"
        >
          <ArrowTopRightOnSquareIcon className="w-4 h-4" />
          <span>Im Browser</span>
        </button>
      </div>
    </div>
  );

  const renderPdfLoading = () => (
    <div className="flex flex-col items-center justify-center h-96 space-y-3">
      <div className="w-12 h-12 border-4 border-warm-200 dark:border-gray-700 rounded-full animate-spin border-t-primary dark:border-t-accent"></div>
      <p className="text-sm text-gray-600 dark:text-gray-400">PDF wird geladen…</p>
    </div>
  );

  const renderContent = () => {
    if (!state.data) return null;

    return (
      <div
        ref={contentRef}
        className="ris-viewer-content prose prose-sm max-w-none dark:prose-invert"
        dangerouslySetInnerHTML={{ __html: sanitizeRisHtml(state.data.html) }}
      />
    );
  };

  // Use insertedHighlightCount (actual visible highlights) if available, otherwise fall back to total
  const highlightCount = state.data?.insertedHighlightCount ?? state.data?.highlights?.highlights?.length ?? 0;
  const fromCache = state.data?.fromCache;

  // PDF-Vorschau steht nur zur Verfügung, wenn das gespeicherte Original ein PDF ist.
  const pdfPreviewUrl = state.data?.pdfPreviewUrl ?? null;
  const passages = state.data?.passages ?? [];
  const pdfMode = Boolean(pdfPreviewUrl) && viewMode === 'pdf';
  // Nur verortete Passagen sind anspringbar, und zwar in Dokumentreihenfolge:
  // nach Seite, bei gleicher Seite nach Rang der Passage.
  const locatedPassages = orderedLocatedPassages(pdfLocations);
  const pdfIndex = locatedPassages.length > 0 ? Math.min(pdfPosition, locatedPassages.length - 1) : 0;
  const activePassageIndex = pdfMode && locatedPassages.length > 0 ? locatedPassages[pdfIndex] : -1;
  const navCount = pdfMode ? locatedPassages.length : highlightCount;
  const navPosition = pdfMode ? pdfIndex : highlightIndex;

  const showPreviousHighlight = () => {
    if (navCount === 0) return;
    const next = (navPosition - 1 + navCount) % navCount;
    if (pdfMode) {
      pdfNavigatedRef.current = true;
      setPdfPosition(next);
      return;
    }
    setHighlightIndex(next);
    scrollToHighlight(next);
  };
  const showNextHighlight = () => {
    if (navCount === 0) return;
    const next = (navPosition + 1) % navCount;
    if (pdfMode) {
      pdfNavigatedRef.current = true;
      setPdfPosition(next);
      return;
    }
    setHighlightIndex(next);
    scrollToHighlight(next);
  };

  return (
    <div
      ref={backdropRef}
      role="dialog"
      aria-modal="true"
      aria-label="RIS-Dokumentansicht"
      className="editorial-modal-backdrop fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={handleBackdropClick}
    >
      <div
        ref={dialogRef}
        className="bg-white dark:bg-gray-800 border border-[var(--cl-border)] rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-warm-200 dark:border-gray-700 bg-warm-100 dark:bg-gray-900">
          <div className="flex-1 min-w-0">
            {/* DR-13 UI-19, UI-32: Titel wie die Dialoge der Anwendung (administration.css .adm-dialog h2), ohne die Kette des RIS. */}
            <h2 className="font-['Georgia',serif] text-[26px] font-normal leading-[1.2] text-gray-900 dark:text-gray-100 truncate">
              {dokumentTitel(state.data?.metadata?.title || 'RIS Dokument')}
            </h2>
            {state.data?.metadata && (
              <div className="flex items-center space-x-3 mt-1 text-sm text-gray-500 dark:text-gray-400">
                <span>{state.data.metadata.gericht}</span>
                <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                <span>{state.data.metadata.geschaeftszahl}</span>
                {state.data.metadata.datum && (
                  <>
                    <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                    <span>{state.data.metadata.datum}</span>
                  </>
                )}
              </div>
            )}
            {state.data?.metadata?.hinweis && (
              <div className="mt-0.5 text-xs text-amber-700 dark:text-amber-500">
                {state.data.metadata.hinweis}
              </div>
            )}
            {(state.data?.metadata?.gesetzesnummer || state.data?.metadata?.dokumentnummer || state.data?.metadata?.imRisSeit || state.data?.metadata?.entscheidungsart || state.data?.metadata?.rechtsgebiet || state.data?.metadata?.rechtssatznummer) && (
              <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                {state.data.metadata.gesetzesnummer && <span>Gesetzesnr. {state.data.metadata.gesetzesnummer}</span>}
                {state.data.metadata.dokumentnummer && <span>Dok. {state.data.metadata.dokumentnummer}</span>}
                {state.data.metadata.imRisSeit && <span>Im RIS seit {state.data.metadata.imRisSeit}</span>}
                {state.data.metadata.entscheidungsart && <span>Entscheidungsart: {state.data.metadata.entscheidungsart}</span>}
                {state.data.metadata.rechtsgebiet && <span>Rechtsgebiet: {state.data.metadata.rechtsgebiet.length > 80 ? state.data.metadata.rechtsgebiet.substring(0, 80) + '...' : state.data.metadata.rechtsgebiet}</span>}
                {state.data.metadata.rechtssatznummer && <span>RS {state.data.metadata.rechtssatznummer}</span>}
              </div>
            )}
          </div>

          <button
            ref={closeButtonRef}
            onClick={closeViewer}
            className="ml-4 p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            aria-label="Schließen"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Toolbar */}
        {state.data && (
          <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
              {navCount > 0 && (
                <span className="flex items-center space-x-1.5">
                  <span className="w-3 h-3 rounded" style={{ backgroundColor: '#FDE047' }}></span>
                  <span>{navCount} relevante Stelle{navCount !== 1 ? 'n' : ''}</span>
                  <button
                    type="button"
                    aria-label="Vorherige relevante Stelle"
                    disabled={navCount <= 1}
                    onClick={showPreviousHighlight}
                    className="ml-1 rounded border border-warm-200 px-1.5 py-0.5 text-xs hover:text-primary disabled:opacity-40"
                  >↑</button>
                  <span role="status" aria-live="polite" aria-label="Aktuelle relevante Stelle" className="min-w-10 text-center text-xs tabular-nums">
                    {navPosition + 1} / {navCount}
                  </span>
                  <button
                    type="button"
                    aria-label="Nächste relevante Stelle"
                    disabled={navCount <= 1}
                    onClick={showNextHighlight}
                    className="rounded border border-warm-200 px-1.5 py-0.5 text-xs hover:text-primary disabled:opacity-40"
                  >↓</button>
                </span>
              )}
              {pdfError && (
                <span className="text-xs text-amber-700 dark:text-amber-500">{pdfError}</span>
              )}
              {fromCache && (
                <span className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                  aus Cache
                </span>
              )}
            </div>

            <div className="flex items-center space-x-2">
              {pdfPreviewUrl && (
                <div
                  role="group"
                  aria-label="Ansicht"
                  className="flex items-center overflow-hidden rounded border border-warm-200 dark:border-gray-600"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setPdfError(null);
                      setViewMode('pdf');
                    }}
                    aria-pressed={viewMode === 'pdf'}
                    className={`px-2.5 py-1 text-xs transition-colors ${viewMode === 'pdf' ? 'bg-primary text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                  >
                    PDF
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('text')}
                    aria-pressed={viewMode === 'text'}
                    className={`px-2.5 py-1 text-xs transition-colors ${viewMode === 'text' ? 'bg-primary text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                  >
                    Text
                  </button>
                </div>
              )}

              <button
                onClick={handleCopyContent}
                title="Text des Dokuments kopieren"
                className="flex items-center space-x-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                {copied ? (
                  <>
                    <CheckIcon className="w-4 h-4 text-green-600" />
                    <span>Kopiert</span>
                  </>
                ) : (
                  <>
                    <ClipboardDocumentIcon className="w-4 h-4" />
                    <span>Kopieren</span>
                  </>
                )}
              </button>

              <button
                onClick={refreshViewer}
                disabled={state.isLoading}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              >
                <ArrowPathIcon className={`w-4 h-4 ${state.isLoading ? 'animate-spin' : ''}`} />
                <span>Aktualisieren</span>
              </button>

              {state.data?.pdfDownloadUrl && (
                <a
                  href={buildApiUrl(state.data.pdfDownloadUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  {state.data?.metadata?.dokumentTyp === 'DOCX' || state.data?.metadata?.dokumentTyp === 'DOC'
                    ? state.data.metadata.dokumentTyp
                    : 'PDF'}
                </a>
              )}

              <button
                onClick={handleOpenExternal}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                <span>Im Browser</span>
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className={`flex-1 overflow-y-auto overscroll-contain bg-white dark:bg-gray-800 ${pdfMode && state.data && !state.isLoading && !state.error ? 'px-2 py-4' : 'p-6'}`}>
          {state.isLoading && renderLoading()}
          {state.error && !state.isLoading && renderError()}
          {state.data && !state.isLoading && !state.error && (
            <>
              {/* Die Vorschau bleibt beim Umschalten eingehängt, damit Datei und
                  Passagensuche nicht erneut laufen. */}
              {pdfPreviewUrl && !pdfError && (
                <div hidden={!pdfMode}>
                  <React.Suspense fallback={renderPdfLoading()}>
                    <DocumentPdfPreview
                      url={pdfPreviewUrl}
                      passages={passages}
                      activeIndex={activePassageIndex}
                      passagePosition={locatedPassages.length > 0 ? pdfIndex + 1 : 0}
                      passageCount={locatedPassages.length}
                      onPassageLocated={handlePassageLocated}
                      onError={handlePdfError}
                    />
                  </React.Suspense>
                </div>
              )}
              <div hidden={pdfMode}>{renderContent()}</div>
            </>
          )}
        </div>

        {/* Footer with highlight legend */}
        {state.data && navCount > 0 && (
          <div className="px-6 py-3 border-t border-warm-200 dark:border-gray-700 bg-warm-100 dark:bg-gray-900">
            <div className="flex items-center justify-center space-x-6 text-xs text-gray-500 dark:text-gray-400">
              <div className="flex items-center space-x-2">
                <span className="w-4 h-4 rounded" style={{ backgroundColor: '#FDE047' }}></span>
                <span>Direkt relevant</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="w-4 h-4 rounded" style={{ backgroundColor: '#FDBA74' }}></span>
                <span>Wichtiger Kontext</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Custom styles for RIS content */}
      <style>{`
        .ris-viewer-content {
          font-family: 'Times New Roman', serif;
          font-size: 14px;
          line-height: 1.6;
        }
        .ris-viewer-content h1,
        .ris-viewer-content h2,
        .ris-viewer-content h3 {
          font-family: 'Times New Roman', serif;
          color: inherit;
        }
        .ris-viewer-content .ErlText {
          margin-bottom: 0.5em;
        }
        .ris-viewer-content .Titel {
          font-weight: bold;
          margin-top: 1em;
        }
        .ris-viewer-content table {
          font-size: 12px;
        }
        /* DR-13 UI-32: die Angaben des RIS (Kurztitel, Inkrafttretensdatum, Abkürzung …) als kompakter Block aus Beschriftung
           und Wert in der Schrift der Anwendung, abgesetzt vom Normtext; die Nummer eines Absatzes mit Abstand zum Text. */
        .ris-viewer-content .documentLinks {
          display: none;
        }
        .ris-viewer-content .p:not(.embeddedContent) {
          margin: 0 0 8px;
          font: 14px/1.4 Inter, system-ui, sans-serif;
        }
        .ris-viewer-content .p h3 {
          margin: 0 0 2px;
          font: 600 12px/1.4 Inter, system-ui, sans-serif;
          letter-spacing: 0;
          color: var(--cl-muted);
        }
        .ris-viewer-content .FloatLeft {
          display: inline-block;
          vertical-align: top;
          margin-right: 32px;
        }
        .ris-viewer-content .embeddedContent {
          margin: 16px 0;
          padding: 12px 0;
          border-top: 1px solid var(--cl-border);
          border-bottom: 1px solid var(--cl-border);
        }
        .ris-viewer-content .Absatzzahl {
          margin-right: 0.35em;
        }
        /* Override RIS document styles for dark mode */
        .dark .ris-viewer-content {
          color: #e5e7eb;
        }
        .dark .ris-viewer-content a {
          color: var(--editorial-olive);
        }
      `}</style>
    </div>
  );
};

export default RISViewerModal;
